import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import { Stack, Button, Dialog, TextField, Typography, DialogTitle, DialogActions, DialogContent } from '@mui/material';

import { useTranslate } from 'src/locales';
import { CommunicationEntityRef } from 'src/services/integration/integrations.types';
import { useSendCommunicationSmsMutation } from 'src/services/integration/integrations.service';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  toNumber: string;
  name?: string;
  entity?: CommunicationEntityRef;
  onClose: () => void;
};

export default function SmsComposeModal({ open, toNumber, name, entity, onClose }: Props) {
  const { t } = useTranslate();
  const [body, setBody] = useState('');
  const [sendSms, { isLoading }] = useSendCommunicationSmsMutation();

  const handleSend = async () => {
    if (!body.trim()) return;
    try {
      await sendSms({ to_number: toNumber, body, ...entity }).unwrap();
      enqueueSnackbar(t('integrations.communications.actions.sms_success'), { variant: 'success' });
      setBody('');
      onClose();
    } catch (e: any) {
      enqueueSnackbar(e?.data?.message || t('integrations.communications.actions.error'), { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('integrations.communications.actions.sms_title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('integrations.communications.actions.sms_to', { to: name ? `${name} (${toNumber})` : toNumber })}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            autoFocus
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('integrations.communications.actions.sms_placeholder')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>{t('common.close')}</Button>
        <LoadingButton variant="contained" loading={isLoading} disabled={!body.trim()} onClick={handleSend}>
          {t('integrations.communications.actions.send')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
